import type { LogPanelConfig } from "@/shared/state/visualization/schema/v1/log-panel";
import type { TopicData } from "@/shared/state/visualization/schema/v1/topic-data";

import type { ColumnDefinition } from "./columnDefinition";
import {
  type ColumnFilterMap,
  type LiveColumnFilters,
  buildLiveColumnFilters,
  migratePersistedColumnFilterKeys,
} from "./state";

/**
 * Derives {@link LiveColumnFilters} from the panel's persisted config and
 * data. `panel.config.columnFilters` is the single source of truth: this runs
 * on every config or data change, so an external config write (AI tool, layout
 * hydration) is reflected without any panel-local copy to reconcile.
 *
 * Legacy key and value shapes are normalized on read only — deriving never
 * writes back, so loading old configs cannot dirty the workspace.
 *
 * Filters for columns `data` doesn't back (a layout applied to a dataset
 * missing that column) are withheld from the live filters but left in config;
 * the next filter edit commits the live map and converges config lazily.
 */
export function deriveLiveColumnFilters(
  savedFilters: LogPanelConfig["columnFilters"],
  data: readonly TopicData[],
  dynamicColumns: ColumnDefinition[],
): LiveColumnFilters {
  const migrated = migratePersistedColumnFilterKeys(savedFilters, data);
  const restored = buildLiveColumnFilters(dynamicColumns, migrated);

  const filters: ColumnFilterMap = {};
  for (const [columnId, filter] of Object.entries(restored.filters)) {
    if (Object.hasOwn(restored.columns, columnId)) {
      filters[columnId] = filter;
    }
  }
  return { filters, columns: restored.columns };
}
